import EquipmentScheduleService from "@/services/scheduleService";
import { rowBucket, isCategoryLockedElsewhere } from "@/utils/equipmentLocks";

const defaultFilters = () => ({
    search: "",
    bucket: "all",
});

const defaultSource = () => ({
    sourceTab: null,
    fromDate: null,
    toDate: null,
});

const defaultAllowSets = () => ({
    has_due_work: null,
    unscheduled: null,
});

export const ScheduleManageStore = {
    namespaced: true,
    state: {
        customer_id: null,
        schedule_id: null,
        isLoading: false,
        equipment: [],
        filters: defaultFilters(),
        source: defaultSource(),
        allowSets: defaultAllowSets(),
    },

    mutations: {
        SET_EQUIPMENT(state, list) {
            state.equipment = Array.isArray(list) ? list : [];
        },
        CLEAR_EQUIPMENT(state) {
            state.equipment = [];
        },
        PATCH_EQUIPMENT(state, { equipment_id, patch }) {
            const idx = state.equipment.findIndex(
                item => item && item.scheduleDetail
                    && item.scheduleDetail.equipment_id === equipment_id
            );
            if (idx === -1) return;
            const row = state.equipment[idx];
            state.equipment.splice(idx, 1, {
                equipment: row.equipment,
                scheduleDetail: { ...row.scheduleDetail, ...patch },
            });
        },
        SET_FILTER(state, { key, value }) {
            if (!(key in state.filters)) return;
            state.filters = { ...state.filters, [key]: value };
        },
        RESET_FILTERS(state) {
            state.filters = defaultFilters();
        },
        SET_CUSTOMER_ID(state, id) {
            state.customer_id = id || null;
        },
        SET_SCHEDULE_ID(state, id) {
            state.schedule_id = id || null;
        },
        SET_LOADING(state, isLoading) {
            state.isLoading = !!isLoading;
        },
        SET_SOURCE(state, { sourceTab, fromDate, toDate }) {
            state.source = {
                sourceTab: sourceTab || null,
                fromDate: fromDate || null,
                toDate: toDate || null,
            };
        },
        CLEAR_SOURCE(state) {
            state.source = defaultSource();
        },
        SET_ALLOW_SET(state, { bucket, ids }) {
            if (!(bucket in state.allowSets)) return;
            state.allowSets = {
                ...state.allowSets,
                [bucket]: Array.isArray(ids) ? new Set(ids) : null,
            };
        },
        CLEAR_ALLOW_SETS(state) {
            state.allowSets = defaultAllowSets();
        },
    },

    actions: {
        async fetchEquipmentForCustomer({ commit, state }) {
            if (!state.customer_id) {
                commit("CLEAR_EQUIPMENT");
                return;
            }
            commit("SET_LOADING", true);
            try {
                const url = buildFetchUrl(state.customer_id, state.schedule_id);
                const response = await EquipmentScheduleService
                    .getEquipmentsAndScheduleByCustomerId(url);
                const list = Array.isArray(response && response.data)
                    ? response.data
                    : (Array.isArray(response) ? response : []);

                const enriched = list.map(item => ({
                    equipment: item && item.equipment
                        ? enrichEquipmentWithExpiry(item.equipment)
                        : null,
                    scheduleDetail: item && item.scheduleDetail ? { ...item.scheduleDetail } : null,
                })).filter(row => row.equipment && row.scheduleDetail);

                commit("SET_EQUIPMENT", enriched);
            } catch (err) {
                console.error("Error fetching equipment for customer:", err);
                commit("SET_EQUIPMENT", []);
            } finally {
                commit("SET_LOADING", false);
            }
        },

        async fetchAllowSetForSource({ commit, state }) {
            const { sourceTab, fromDate, toDate } = state.source;
            const customer_id = state.customer_id;
            if (!sourceTab || !customer_id) return;
            if (sourceTab === "follow_up") return;

            const tabKey = sourceTab === "unbooked" ? "unscheduled" : "all_expiring";
            const bucket = tabKey === "unscheduled" ? "unscheduled" : "has_due_work";

            const url = buildAllowSetUrl({ customer_id, tabKey, fromDate, toDate });
            try {
                const response = await EquipmentScheduleService.searchEquipmentUpComingService(url);
                const list = Array.isArray(response && response.data)
                    ? response.data
                    : (Array.isArray(response) ? response : []);
                const ids = list.map(row => row && (row.equipment_id || row._id)).filter(Boolean);
                commit("SET_ALLOW_SET", { bucket, ids });
            } catch (err) {
                console.error("Error fetching source allow-set:", err);
                commit("SET_ALLOW_SET", { bucket, ids: null });
            }
        },

        applyRouteContext({ commit, dispatch }, { sourceTab, fromDate, toDate }) {
            commit("SET_SOURCE", { sourceTab, fromDate, toDate });
            dispatch("fetchAllowSetForSource");
            if (sourceTab) {
                let bucket = "has_due_work";
                if (sourceTab === "unbooked") bucket = "unscheduled";
                else if (sourceTab === "follow_up") bucket = "follow_up";
                commit("SET_FILTER", { key: "bucket", value: bucket });
            }
        },

        toggleCategory({ commit, state }, { equipment_id, category, value }) {
            if (!equipment_id || !category) return;
            const row = state.equipment.find(
                item => item.scheduleDetail && item.scheduleDetail.equipment_id === equipment_id
            );
            if (!row) return;

            if (value && isCategoryLockedElsewhere(row.equipment, category, state.schedule_id)) {
                return;
            }

            commit("PATCH_EQUIPMENT", {
                equipment_id,
                patch: { [`is_${category}`]: !!value },
            });
        },

        setFilter({ commit }, { key, value }) {
            commit("SET_FILTER", { key, value });
        },
        resetFilters({ commit }) { commit("RESET_FILTERS"); },

        async updateCurrentCustomerId({ commit, dispatch }, currentCustomerId) {
            commit("SET_CUSTOMER_ID", currentCustomerId);
            await Promise.all([
                dispatch("fetchEquipmentForCustomer"),
                dispatch("fetchAllowSetForSource"),
            ]);
        },

        updateCurrentScheduleId({ commit }, scheduleId) {
            commit("SET_SCHEDULE_ID", scheduleId);
        },

        clearAll({ commit }) {
            commit("CLEAR_EQUIPMENT");
            commit("RESET_FILTERS");
            commit("CLEAR_SOURCE");
            commit("CLEAR_ALLOW_SETS");
            commit("SET_CUSTOMER_ID", null);
            commit("SET_SCHEDULE_ID", null);
        },
    },

    getters: {
        filteredEquipment(state, getters) {
            const f = state.filters;
            const search = (f.search || "").trim().toLowerCase();
            const bucket = f.bucket || "all";
            const currentScheduleId = state.schedule_id;
            const allowSet = state.allowSets[bucket] || null;

            return state.equipment.filter(item => {
                const eq = item.equipment || {};
                const sd = item.scheduleDetail || {};

                if (search) {
                    const hay = [
                        eq.product_code, eq.name, eq.description,
                        eq.serial_number, eq.equipment_id,
                        eq.product_category, eq.service_agent_name,
                    ].map(x => (x || "").toString().toLowerCase()).join(" ");
                    if (!hay.includes(search)) return false;
                }

                if (bucket && bucket !== "all") {
                    if (allowSet) {
                        if (!allowSet.has(eq.equipment_id)) return false;
                    } else {
                        const b = rowBucket(eq, sd, currentScheduleId);
                        if (b !== bucket) return false;
                    }
                }

                return true;
            });
        },

        bucketCounts(state) {
            const counts = {
                all: state.equipment.length,
                has_due_work: 0,
                on_this_schedule: 0,
                booked_elsewhere: 0,
                unscheduled: 0,
                follow_up: 0,
            };
            const currentScheduleId = state.schedule_id;
            for (const item of state.equipment) {
                const eq = item.equipment || {};
                const sd = item.scheduleDetail || {};
                const b = rowBucket(eq, sd, currentScheduleId);
                if (b in counts) counts[b]++;
            }
            for (const bucket of ["has_due_work", "unscheduled"]) {
                const allow = state.allowSets[bucket];
                if (allow) {
                    let n = 0;
                    for (const item of state.equipment) {
                        const id = item.equipment && item.equipment.equipment_id;
                        if (id && allow.has(id)) n++;
                    }
                    counts[bucket] = n;
                }
            }
            return counts;
        },
    },
};

function buildFetchUrl(customer_id, schedule_id) {
    const params = [];
    if (schedule_id) params.push(`scheduleId=${encodeURIComponent(schedule_id)}`);
    params.push(`ts=${Date.now()}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return `/${customer_id}/equipment-with-schedule${qs}`;
}

function buildAllowSetUrl({ customer_id, tabKey, fromDate, toDate }) {
    const params = [];
    params.push(`ownerId=${encodeURIComponent(customer_id)}`);
    params.push(`tab=${encodeURIComponent(tabKey)}`);
    if (fromDate) params.push(`fromDate=${encodeURIComponent(fromDate)}`);
    if (toDate) params.push(`toDate=${encodeURIComponent(toDate)}`);
    params.push("noPaging=TRUE");
    params.push(`ts=${Date.now()}`);
    return `?${params.join("&")}`;
}

function enrichEquipmentWithExpiry(equipment) {
    const calibrations = equipment.calibrations || [];
    let latestCalibration = null;
    let latestService = null;

    calibrations.forEach(cal => {
        if (cal.job_type === "CALIBRATION") {
            if (!latestCalibration || cal.cert_expiry_date > latestCalibration)
                latestCalibration = cal.cert_expiry_date;
        }
        if (cal.job_type === "SERVICE") {
            if (!latestService || cal.cert_expiry_date > latestService)
                latestService = cal.cert_expiry_date;
        }
    });

    let latestWarranty = equipment.warranty_expiry || null;
    if (equipment.contracts && equipment.contracts.length > 0) {
        equipment.contracts.forEach(c => {
            if (c.end_date && (!latestWarranty || c.end_date > latestWarranty)) {
                latestWarranty = c.end_date;
            }
        });
    }

    return {
        ...equipment,
        latestCalibrationExpiry: latestCalibration,
        latestServiceExpiry: latestService,
        latestWarrantyExpiry: latestWarranty,
    };
}
